import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AchMethodModel } from '../../../customer/customer.model';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxMatErrors } from 'ngx-mat-errors';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgIf } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ACH_COMPANY_NAME_PATTERN, ACH_PERSON_NAME_PATTERN } from '../../../../shared/utils/ach-name-patterns';

interface AchDetailsForm {
  account: FormControl<string | null>;
  confirmAccount: FormControl<string | null>;
  abaRoute: FormControl<string | null>;
  accountHolderName: FormControl<string | null>;
  accountHolderFirstName: FormControl<string | null>;
  accountHolderLastName: FormControl<string | null>;
  description: FormControl<string | null>;
  accountType: FormControl<'CONSUMER' | 'BUSINESS' | null>;
  isDefault: FormControl<boolean | null>,
}

@Component({
  selector: 'app-ach-details-form',
  standalone: true,
  imports: [
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatSlideToggleModule,
    NgxMaskDirective,
    NgxMatErrors,
    MatCheckboxModule,
    NgIf,
    MatSelectModule,
    MatOptionModule
  ],
  templateUrl: './ach-details-form.component.html',
  host: {
    class: 'app-ach-details-form'
  },
  providers: [
    provideNgxMask()
  ]
})
export class AchDetailsFormComponent implements OnInit {
  @ViewChild('accountInput') accountInput!: ElementRef;
  @ViewChild('routingInput') routingInput!: ElementRef;
  @ViewChild('accountHolderNameInput') accountHolderNameInput!: ElementRef;
  @ViewChild('accountTypeInput') accountTypeInput!: ElementRef;
  @ViewChild('descriptionInput') descriptionInput!: ElementRef;
  @ViewChild('isDefaultInput') isDefaultInput!: ElementRef;

  
  @Input() data: { payment: AchMethodModel | null } = { payment: null };
  
  @Input() hideDefaultOption = false;
  
  accPrefix = "";
  isViewMode = false;

  isAvsInvalid = false;
  invalidMessage = 'This is the invalid message';

  accountMask = '00000000';  // 5-17 digits
  routingMask = '000000000';         // 9 digits

  isAccountInvalid = false;
  isRoutingInvalid = false;
  isAchInvalid = false;

  form = new FormGroup<AchDetailsForm>({
    account: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(17),
      Validators.pattern(/^\d{5,17}$/),
      (control) => this.isAchInvalid ? { achInvalid: true } : null
    ]),
    confirmAccount: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(17),
      Validators.pattern(/^\d{5,17}$/),
      (control) => {
        if (control.value !== this.form?.get('account')?.value) {
          return { accountMismatch: true };
        }
        return null;
      }
    ]),
    abaRoute: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(9),
      Validators.maxLength(9),
      Validators.pattern(/^\d{9}$/)
    ]),
    accountHolderName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(40),
      Validators.pattern(ACH_COMPANY_NAME_PATTERN)
    ]),
    accountHolderFirstName: new FormControl<string | null>(null),
    accountHolderLastName: new FormControl<string | null>(null),
    description: new FormControl<string | null>('ACH PAYMENT', [
      Validators.maxLength(25)
    ]),
    accountType: new FormControl<'CONSUMER' | 'BUSINESS' | null>('BUSINESS', [
      Validators.required
    ]),
    isDefault: new FormControl(false),
  });

  private readonly nameValidators = [Validators.minLength(2), Validators.maxLength(40), Validators.pattern(ACH_COMPANY_NAME_PATTERN)];
  private readonly firstLastNameValidators = [Validators.minLength(1), Validators.maxLength(20), Validators.pattern(ACH_PERSON_NAME_PATTERN)];

  /** Business needs a company name; Consumer needs first+last — see PaymentAccountRequest's ACH validation in FTE. */
  private applyAccountTypeValidators(type: 'CONSUMER' | 'BUSINESS' | null): void {
    const { accountHolderName, accountHolderFirstName, accountHolderLastName } = this.form.controls;
    if (type === 'CONSUMER') {
      accountHolderName.setValidators(this.nameValidators);
      accountHolderFirstName.setValidators([Validators.required, ...this.firstLastNameValidators]);
      accountHolderLastName.setValidators([Validators.required, ...this.firstLastNameValidators]);
    } else {
      accountHolderName.setValidators([Validators.required, ...this.nameValidators]);
      accountHolderFirstName.setValidators(this.firstLastNameValidators);
      accountHolderLastName.setValidators(this.firstLastNameValidators);
    }
    accountHolderName.updateValueAndValidity({ emitEvent: false });
    accountHolderFirstName.updateValueAndValidity({ emitEvent: false });
    accountHolderLastName.updateValueAndValidity({ emitEvent: false });
  }

  getErrors() {
    return JSON.stringify(this.form.get('streetAddress')?.errors);
  }

  bypassAVSValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isAvsInvalid && this.form.get('bypassAVS')?.value==false) {
      return { 'bypassAVSInvalid': true };
    } 
    return null;
  }

  async onAccountFilled() {
    this.accountHolderNameInput.nativeElement.focus();
  }

  ngOnInit(): void {
    this.form.controls.accountType.valueChanges.subscribe((type) => this.applyAccountTypeValidators(type));
    this.applyAccountTypeValidators(this.form.controls.accountType.value);

    if (this.data?.payment) {
      this.form.patchValue({
        account: this.data.payment.account,
        abaRoute: this.data.payment.abaRoute,
        accountHolderName: this.data.payment.accountHolderName,
        accountHolderFirstName: this.data.payment.accountHolderFirstName,
        accountHolderLastName: this.data.payment.accountHolderLastName,
        accountType: this.data.payment.accountType as "CONSUMER" | "BUSINESS",
        isDefault: this.data.payment.isDefault ?? false
      });
      this.isViewMode = true;
    }
  }

  getFormData(): AchMethodModel {
    const formValue = this.form.getRawValue();
    const isConsumer = formValue.accountType === 'CONSUMER';
    return {
      account: formValue.account!,
      abaRoute: formValue.abaRoute!,
      accountHolderName: isConsumer ? undefined : formValue.accountHolderName?.slice(0, 22),
      accountHolderFirstName: isConsumer ? formValue.accountHolderFirstName?.slice(0, 20) : undefined,
      accountHolderLastName: isConsumer ? formValue.accountHolderLastName?.slice(0, 20) : undefined,
      accountType: formValue.accountType!,
      description: formValue.description!,
      isDefault: formValue.isDefault ?? false
    };
  }

  setAchInvalid(isInvalid: boolean, message?: string) {
    this.isAchInvalid = isInvalid;
    this.invalidMessage = message || '';
    if (isInvalid) {
      this.form.controls.account.updateValueAndValidity();
      this.form.controls.abaRoute.updateValueAndValidity();
    }
  }
}
